import React, { useRef, useEffect, useState } from 'react';
import { Box, TextField, IconButton, Typography, keyframes, Collapse, Chip } from '@mui/material';
import { FontIcon } from './FontIcon';
import Markdown from 'react-markdown';
import type { Assistant as Interface } from '../../design/components';
import { AdaIcon } from '../shared/AdaIcon';

const bounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-4px); }
`;

const TypingIndicator = () => (
  <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center' }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          bgcolor: 'text.secondary',
          animation: `${bounce} 1.4s ease-in-out infinite`,
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ))}
  </Box>
);

// Pre-process content to fix malformed ```steps blocks and merge consecutive ones
const preprocessSteps = (content: string): string => {
  // First, extract all steps from all blocks
  const allSteps: string[] = [];
  const processed = content.replace(/```steps\s*([\s\S]*?)```/g, (_, stepsContent) => {
    let steps = stepsContent.trim().split('\n').filter(Boolean);
    // If only one "step" that looks like multiple run together, try to split
    if (steps.length === 1 && steps[0].length > 50) {
      steps = steps[0].split(/(?<=[a-z.!?])\s*(?=[A-Z][a-z]+(?:ed|ing|d)\s)/).filter(Boolean);
    }
    allSteps.push(...steps);
    return '{{STEPS_PLACEHOLDER}}';
  });

  if (allSteps.length === 0) return content;

  // Deduplicate steps while preserving order
  const uniqueSteps = [...new Set(allSteps)];

  // Replace first placeholder with consolidated block, remove the rest
  let first = true;
  return processed.replace(/\{\{STEPS_PLACEHOLDER\}\}/g, () => {
    if (first) {
      first = false;
      return '\n\n```steps\n' + uniqueSteps.join('\n') + '\n```\n\n';
    }
    return '';
  });
};

const CollapsibleSteps = ({ children }: { children: string }) => {
  const [expanded, setExpanded] = useState(false);
  const steps = children.trim().split('\n').filter(Boolean);
  const stepCount = steps.length;

  return (
    <Box sx={{ my: 0.5 }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          color: 'text.secondary',
          fontSize: '0.8rem',
          '&:hover': { color: 'text.primary' },
        }}
      >
        <FontIcon name={expanded ? 'ExpandLess' : 'ExpandMore'} size="small" />
        {stepCount} {stepCount === 1 ? 'step' : 'steps'} completed
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ pl: 2.5, pt: 0.5, fontSize: '0.8rem', color: 'text.secondary' }}>
          {steps.map((step, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 0.5 }}>
              <span>•</span>
              <span>{step}</span>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export const Assistant: React.FC<Interface> = ({ state, onSend, disabled }) => {
  const [messages] = state || [[], () => {}];
  const [input, setInput] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if ((!input.trim() && files.length === 0) || disabled) return;
    const content = input.trim() || `Attached ${files.length} file${files.length === 1 ? '' : 's'}`;
    const sendFiles = files.length > 0 ? files : undefined;
    setInput('');
    setFiles([]);
    const unused = await onSend?.(content, sendFiles);
    if (unused?.length) setFiles(unused);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files!)]);
    }
    e.target.value = '';
  };

  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files).filter(f =>
      f.type.startsWith('image/') || f.type === 'application/pdf'
    );
    if (dropped.length > 0) setFiles(prev => [...prev, ...dropped]);
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'grey.50', position: 'relative' }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false); }}
      onDrop={handleDrop}
    >
      {dragOver && (
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 10,
          bgcolor: 'rgba(25, 118, 210, 0.08)',
          border: '2px dashed',
          borderColor: 'primary.main',
          borderRadius: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <Typography variant="body2" color="primary">Drop files here</Typography>
        </Box>
      )}
      {/* Header */}
      <Box sx={{
        px: 2,
        py: 1.5,
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        bgcolor: 'background.paper',
      }}>
        <AdaIcon size={20} working={disabled} />
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Ada</Typography>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0 }}>
        {messages.map((msg) => (
          <Box
            key={msg.id}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <Box
              sx={{
                py: 1.25,
                px: 1.75,
                maxWidth: '85%',
                bgcolor: msg.error
                  ? 'error.lighter'
                  : msg.role === 'user'
                  ? 'primary.main'
                  : 'background.paper',
                color: msg.error
                  ? 'error.dark'
                  : msg.role === 'user'
                  ? 'primary.contrastText'
                  : 'text.primary',
                borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                boxShadow: msg.role === 'user' ? 'none' : '0 1px 2px rgba(0,0,0,0.08)',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
                '& ul, & ol': { m: 0, pl: 2.5, mb: 1 },
                '& li': { mb: 0.25 },
                '& code': {
                  bgcolor: msg.role === 'user' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.06)',
                  px: 0.5,
                  borderRadius: 0.5,
                  fontFamily: 'monospace',
                  fontSize: '0.85em',
                },
                '& pre': {
                  bgcolor: msg.role === 'user' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.04)',
                  p: 1,
                  borderRadius: 1,
                  overflow: 'auto',
                },
              }}
            >
              {msg.role === 'user' ? (
                <>
                  {msg.files && msg.files.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: msg.content ? 0.75 : 0 }}>
                      {msg.files.map((f, i) => (
                        <Chip
                          key={i}
                          label={f.name}
                          size="small"
                          icon={<FontIcon name={f.type.startsWith('image/') ? 'Image' : 'Description'} size="small" />}
                          sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit', '& .MuiChip-icon': { color: 'inherit' } }}
                        />
                      ))}
                    </Box>
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {msg.content}
                  </Typography>
                </>
              ) : (
                <>
                  {msg.content && (
                    <Markdown
                      components={{
                        code({ className, children }) {
                          if (className === 'language-steps') {
                            return <CollapsibleSteps>{String(children)}</CollapsibleSteps>;
                          }
                          return <code className={className}>{children}</code>;
                        },
                        pre({ children }) {
                          // Check if this pre contains a steps code block
                          const child = React.Children.toArray(children)[0];
                          if (React.isValidElement(child) && (child.props as { className?: string })?.className === 'language-steps') {
                            return <>{children}</>;
                          }
                          return <pre>{children}</pre>;
                        },
                      }}
                    >
                      {preprocessSteps(msg.content)}
                    </Markdown>
                  )}
                  {(msg.streaming || !msg.content) && <TypingIndicator />}
                </>
              )}
            </Box>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
        {files.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {files.map((f, i) => (
              <Chip
                key={i}
                label={f.name}
                size="small"
                icon={<FontIcon name={f.type.startsWith('image/') ? 'Image' : 'Description'} size="small" />}
                onDelete={() => setFiles(prev => prev.filter((_, j) => j !== i))}
              />
            ))}
          </Box>
        )}
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            size="small"
            sx={{ mr: 0.5 }}
          >
            <FontIcon name="AttachFile" size="small" />
          </IconButton>
          <TextField
            fullWidth
            variant="filled"
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            size="small"
            hiddenLabel
            sx={{
              '& .MuiFilledInput-root': {
                borderRadius: 1,
                pr: 5,
                '&::before, &::after': { display: 'none' },
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={(!input.trim() && files.length === 0) || disabled}
            size="small"
            sx={{
              position: 'absolute',
              right: 4,
            }}
          >
            <FontIcon name="Send" size="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
